import { View } from 'react-native'
import { useTranslation } from 'react-i18next'
import SummaryItems from '@order/Summary/SummaryItems'
import { SavedOrder } from '@order/hooks/state/orderState'
import { useSavedOrderSummary } from '@order/hooks/useOrderSummary'
import ModalWide from '@ui/components/Modal/variants/ModalWide'
import Typography from '@ui/typography'
import PriceSummary from '@order/PriceSummary'
import DiscountDetailsRow from '@screens/order/discounts/DiscountDetailsRow'
import { useOrderResultTotal } from '@order/hooks/useOrderTotal'
import { useTotalDiscountAmount } from '@order/useDiscounts'
import { PriceSummaryTemplate } from '@order/PriceSummary/types'
import DetailRow from '@ui/elements/DetailRow'
import PriceInfo from '@/price/PriceInfo'
import { useDealSettingsById } from '@order/hooks/useDealSettings'
import { lineBreakToSpace } from 'utils/string'
import { useOrderState } from '@order/hooks/useOrderState'
import useSession from 'hooks/useSession'
import { resolveDealsContext } from 'hooks/useSession'
import { getConvertedPrice, shouldShowMonthly } from '@ca/common/types'

export interface Preview {
  orderId: string
  itemId?: string
}

interface Props {
  preview: Preview | null
  orders: SavedOrder[]
  onClose?: () => void
  children?: React.ReactNode
  template?: PriceSummaryTemplate
  onDiscountPress?: () => void
}

export default function SummaryModal(props: Props) {
  const { preview, orders, onClose, children } = props
  const order = orders.find((o) => o.id === preview?.orderId)

  return (
    <ModalWide open={!!order} close={onClose} hideBottomSpace>
      <View className="flex flex-col gap-8 p-8">
        {order && preview && (
          <SummaryModalBody {...props} order={order}>
            {children}
          </SummaryModalBody>
        )}
      </View>
    </ModalWide>
  )
}

interface SummaryModalBodyProps extends Props {
  order: SavedOrder
  withHeader?: boolean
}

export const useCurrentSummaryModal = () => {
  const orderId = 'current'
  // const orders = useCartState((s) => s.orders)
  const orderState = useOrderState((s) => s.order)
  const result = useOrderState((s) => s.result)
  const discounts = useOrderState((s) => s.discounts)
  const deals = useSession((s) => s.deals)

  const order = {
    id: orderId,
    order: orderState,
    result,
    deals: resolveDealsContext(deals),
    discounts,
  } satisfies SavedOrder

  return { preview: { orderId }, orders: [order], order }
}

export function SummaryModalBody(props: SummaryModalBodyProps) {
  const { t } = useTranslation(['common'])
  const { withHeader = true } = props
  const { order, result, deals, discounts } = props.order
  const summary = useSavedOrderSummary(order, deals)
  const deal = deals.base
  const dealName = deal?.dealName || t('common:deal')
  const dealSettings = useDealSettingsById(deal?.dealId)

  const total = useOrderResultTotal({
    result,
    order,
    deals,
  })

  const discountAmount = useTotalDiscountAmount({
    orderTotal: total,
    discounts,
  })

  const isAutoDiscount =
    result?.isValid && discountAmount.auto != null && discountAmount.auto != 0

  const Header = (
    <View className="flex w-full flex-row items-center justify-between">
      <View className="flex w-2/3 flex-col items-start justify-start gap-1">
        <Typography variant="SMB" className="uppercase color-text/25">
          {t(`common:configuration`)}
        </Typography>
        <Typography variant="XLB" className="truncate" numberOfLines={1}>
          {lineBreakToSpace(dealName)}
        </Typography>
      </View>

      <View>
        <PriceSummary
          template={props.template || 'configuration'}
          installment={total.showMonthly ? dealSettings.priceSuffix : undefined}
          price={discountAmount.discountedDisplay}
          className="p-0"
        />
      </View>
    </View>
  )

  const showDiscounts = discounts.length > 0 || !!props.onDiscountPress

  const hasSummary =
    !!summary?.items?.length || !!order?.items?.length || !!result

  const showMonthly = shouldShowMonthly(dealSettings)

  return (
    <>
      <View
        className="flex flex-col gap-8"
        // style={{ paddingBottom: spaceBottom }}
      >
        {withHeader && <View className="px-8 pb-4 pt-4">{Header}</View>}
        {hasSummary && (
          <View className="flex flex-col overflow-hidden rounded-xl border border-black/10">
            <SummaryItems
              summary={summary || null}
              result={result || null}
              frames={order?.items || []}
              deals={deals}
              order={order}
              expandIds={props.preview?.itemId ? [props.preview.itemId] : []}
            />
            {showDiscounts && (
              <DiscountDetailsRow
                deals={deals}
                discounts={discounts}
                order={order}
                result={result}
                handleOpen={props.onDiscountPress}
                settings={dealSettings}
              />
            )}
            {isAutoDiscount && (
              <DetailRow
                success
                title={t('summary:label.discountsAuto')}
                icon="gift"
              >
                <PriceInfo
                  suffix={showMonthly ? dealSettings.priceSuffix : undefined}
                  value={
                    showMonthly && discountAmount.auto
                      ? getConvertedPrice(discountAmount.auto, dealSettings)
                      : discountAmount.auto
                  }
                  size="default"
                />
              </DetailRow>
            )}
          </View>
        )}
        {props.children}
      </View>
    </>
  )
}
